import {
  formatCents,
  formatCount,
  formatDay,
  isValidIban,
  isValidIco,
  parseMoneyToCents,
  percentageOfCents,
  rateOf,
  tt,
  type MoneyInput,
} from '@agos/shared'

/**
 * A supplier's invoice — a document this restaurant *received*, which is the whole point.
 *
 * **FB-090.** Legacy's form recomputes the three header totals out of the lines and posts
 * them on every save:
 *
 * ```js
 * const totals = useMemo(() => ({
 *   net:   +lines.reduce((s, l) => s + l.lineNet,   0).toFixed(2),
 *   vat:   +lines.reduce((s, l) => s + l.lineVat,   0).toFixed(2),
 *   gross: +lines.reduce((s, l) => s + l.lineGross, 0).toFixed(2),
 * }), [lines])
 * // …
 * const payload = { …, totalNet: totals.net, totalVat: totals.vat, totalGross: totals.gross }
 * ```
 *
 * An outgoing invoice is ours to compute. An incoming one is not: the header says what the
 * supplier will be paid, and `AutoPayService.buildSepaBatch` pays `invoice.totalGross()`.
 * Opening `20260364` to correct its constant symbol and pressing save moves 137,86 € to
 * 137,87 €, because TRADE TECH computes VAT on the whole base (22,01 €) and the sum of the
 * per-line VAT is 22,02 €. The PDF import makes it worse: the parser reads the supplier's
 * printed total, `mapParsedToDraft` copies it into the draft, and then the form throws it
 * away in favour of the sum of whatever rows the parser managed to find. The parser even
 * warns when those two disagree by more than 1 % — and the client's answer to that warning
 * is to overwrite the total with the sum it just warned about.
 *
 * So: the header here is the supplier's figure. It is displayed beside the sum of the
 * lines, the difference is named, and replacing it with the lines' sum is a button somebody
 * presses on purpose.
 *
 * **A line's stated total is also the supplier's.** LUNYS bills 10,77 kg at a printed
 * 5,3333 €/kg for 57,46 € — and 10,77 × 5,3333 is 57,44. The printed unit price is a
 * four-decimal rounding of the real rate, so recomputing the line from it loses two cents
 * (FB-056, from the other direction). Stated figures are kept; the arithmetic is only
 * redone for a line somebody edits.
 */

// ─── shapes ──────────────────────────────────────────────────────────────────

export interface InvoiceRow {
  id?: number
  supplierId?: number | null
  supplierName?: string | null
  invoiceNumber?: string
  variableSymbol?: string | null
  constantSymbol?: string | null
  issueDate?: string | null
  dueDate?: string | null
  deliveryDate?: string | null
  paidAt?: string | null
  paymentForm?: string | null
  iban?: string | null
  totalNet?: MoneyInput
  totalVat?: MoneyInput
  totalGross?: MoneyInput
  currency?: string
  status?: string
  autoPaymentScheduled?: boolean
  /**
   * When this invoice was last put into a SEPA batch, or null — `BACKEND.md` §10A.19.
   *
   * Downloading a batch records a `queued` payment attempt per invoice. The server refuses a
   * second batch on the strength of it; this is here so the row can say *why* it is refused
   * rather than the invoice quietly going missing from a file nobody can account for.
   */
  queuedAt?: string | null
  notes?: string | null
  approvedAt?: string | null
  approvedBy?: number | null
  pdfBlobKey?: string | null
  attachmentBlobKey?: string | null
}

export interface InvoiceLine {
  id?: number
  supplierItemCode?: string | null
  stockItemId?: number | null
  itemName?: string
  countryOfOrigin?: string | null
  /** `"3.000"` off the wire, a number off a form. A multiplicand either way. */
  qty?: MoneyInput
  unit?: string
  unitPriceBeforeDiscount?: MoneyInput
  unitPriceAfterDiscount?: MoneyInput
  discountPct?: MoneyInput
  vatRate?: MoneyInput
  lineNet?: MoneyInput
  lineVat?: MoneyInput
  lineGross?: MoneyInput
  sortOrder?: number
  /** 0…1 from the PDF parser. Absent on a line a person typed. */
  confidence?: number | null
}

export interface InvoiceDetail extends InvoiceRow {
  specificSymbol?: string | null
  orderNumber?: string | null
  lines?: InvoiceLine[]
}

export type InvoiceStatus = 'draft' | 'confirmed' | 'paid' | 'cancelled'

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: tt('Koncept'),
  confirmed: tt('Potvrdená'),
  paid: tt('Zaplatená'),
  cancelled: tt('Zrušená'),
}

export type BadgeColor = 'zinc' | 'blue' | 'emerald' | 'red' | 'amber'

const STATUS_COLORS: Record<InvoiceStatus, BadgeColor> = {
  draft: 'zinc',
  confirmed: 'blue',
  paid: 'emerald',
  cancelled: 'red',
}

export function statusLabel(status: string | undefined): string {
  if (!status) return ''
  return STATUS_LABELS[status as InvoiceStatus] ?? status
}

export function statusColor(status: string | undefined): BadgeColor {
  return STATUS_COLORS[status as InvoiceStatus] ?? 'zinc'
}

// ─── the arithmetic, in cents ────────────────────────────────────────────────

export interface Totals {
  netCents: number
  vatCents: number
  grossCents: number
}

/**
 * What the line says it comes to.
 *
 * The supplier's own figures, parsed to cents and not second-guessed. `grossCents` falls
 * back to net + VAT when the document omitted it, which is the only figure derived here.
 */
export function statedLineTotals(line: InvoiceLine): Totals {
  const netCents = parseMoneyToCents(line.lineNet)
  const vatCents = parseMoneyToCents(line.lineVat)
  const stated = parseMoneyToCents(line.lineGross)
  return { netCents, vatCents, grossCents: stated === 0 ? netCents + vatCents : stated }
}

/**
 * What the line's quantity and unit price come to — for a line somebody is editing.
 *
 * Both multiplicands keep every decimal they were given and only the product becomes money
 * (FB-056). VAT is a percentage of the rounded net, matching what every supplier in the
 * data prints.
 */
export function computedLineTotals(line: InvoiceLine): Totals {
  const qty = rateOf(line.qty)
  // Deliberately *not* `parseMoneyToCents`: these unit prices carry four decimals —
  // 5,3333 €/kg, 13,0252 €/ks — and rounding the multiplicand to the cent before
  // multiplying is the thing FB-056 forbids. Rounding 1,419 €/kg to 1,42 € and multiplying
  // by 22,81 kg gives 32,39 € against the supplier's 32,37 €.
  const unitPrice = rateOf(line.unitPriceAfterDiscount)
  const netCents = roundCents(qty * unitPrice * 100)
  const vatCents = percentageOfCents(netCents, line.vatRate ?? 0)
  return { netCents, vatCents, grossCents: netCents + vatCents }
}

// `rateOf` — FB-056's multiplicand reader — moved to `@agos/shared` when `manager-reporting`'s
// analytics needed it too: §5 says anything two modules need lives in packages/*.

function roundCents(value: number): number {
  if (!Number.isFinite(value)) return 0
  return value < 0 ? -Math.round(-value) : Math.round(value)
}

/**
 * How far a line's stated net is from its quantity times its unit price.
 *
 * Not automatically an error: LUNYS's `10,77 kg × 5,3333 €` prints as 57,46 € because the
 * unit price on the page is a rounded rate. Worth showing, because the other explanation
 * is a mis-keyed quantity.
 */
export function lineNetDriftCents(line: InvoiceLine): number {
  return statedLineTotals(line).netCents - computedLineTotals(line).netCents
}

/** The sum of what the lines say — integer, so it cannot drift. */
export function lineSum(lines: InvoiceLine[]): Totals & { lineCount: number } {
  let netCents = 0
  let vatCents = 0
  let grossCents = 0
  for (const line of lines) {
    const totals = statedLineTotals(line)
    netCents += totals.netCents
    vatCents += totals.vatCents
    grossCents += totals.grossCents
  }
  return { netCents, vatCents, grossCents, lineCount: lines.length }
}

/** The header as the supplier stated it. The figure that gets paid. */
export function headerTotals(invoice: InvoiceRow): Totals {
  return {
    netCents: parseMoneyToCents(invoice.totalNet),
    vatCents: parseMoneyToCents(invoice.totalVat),
    grossCents: parseMoneyToCents(invoice.totalGross),
  }
}

/**
 * Whether the header's own three figures add up.
 *
 * Bidfood's `126103376` states 58,06 € net and 11,03 € VAT and 69,10 € to pay, and
 * 58,06 + 11,03 is 69,09. One cent, on the figure the SEPA batch transfers.
 */
export function headerAddsUp(invoice: InvoiceRow): boolean {
  const header = headerTotals(invoice)
  return header.netCents + header.vatCents === header.grossCents
}

export interface HeaderDelta extends Totals {
  agrees: boolean
}

/** The header minus the lines, per figure. Zero throughout means they agree. */
export function headerDelta(invoice: InvoiceDetail): HeaderDelta {
  const header = headerTotals(invoice)
  const lines = lineSum(invoice.lines ?? [])
  const delta = {
    netCents: header.netCents - lines.netCents,
    vatCents: header.vatCents - lines.vatCents,
    grossCents: header.grossCents - lines.grossCents,
  }
  return {
    ...delta,
    agrees: delta.netCents === 0 && delta.vatCents === 0 && delta.grossCents === 0,
  }
}

/**
 * Whether this invoice's lines and totals may still be changed — `BACKEND.md` §10A.22.
 *
 * Only while it is a draft. Confirming posts the lines to stock — a receipt, a movement per mapped
 * line and a refreshed cost price — so afterwards they are the record of what was received, and the
 * totals are what was approved and paid against. The server refuses both; this is so the form does
 * not offer a field whose save would be rejected.
 *
 * **The header is a separate question** — see `identifiersEditable`. Correcting a wrong invoice
 * number after confirming is legitimate and touches nothing that was posted.
 */
export function amountsEditable(invoice: InvoiceRow): boolean {
  return (invoice.status ?? 'draft') === 'draft'
}

/**
 * Whether the invoice may be opened for editing at all — §10A.22.
 *
 * A `paid` or `cancelled` invoice is closed; anything else can at least have its number, symbols and
 * dates corrected. The screen hid the button for paid and cancelled — a rule the server never had —
 * and offered it for `confirmed`, which is exactly when the goods are already in stock. Both were
 * wrong in different directions: the button belongs on a confirmed invoice, and what it opens does
 * not include the amounts.
 */
export function identifiersEditable(invoice: InvoiceRow): boolean {
  return invoice.status !== 'paid' && invoice.status !== 'cancelled'
}

/** An invoice with no lines at all cannot disagree with them; it has nothing to compare. */
export function hasLines(invoice: InvoiceDetail): boolean {
  return (invoice.lines ?? []).length > 0
}

/**
 * The same question of a form being typed — and the form is where it was being got wrong.
 *
 * A blank new invoice has a blank header and no lines, so every difference is zero and
 * `formDelta` reports `agrees`. The screen drew that as a green *Súhlasí so súčtom riadkov* —
 * telling a manager the lines agree with the header before either exists.
 *
 * **Counted the way `formDelta` counts**: an unnamed row contributes nothing to the sum, so it
 * is not a line yet. Any other rule would make the message appear the moment somebody presses
 * *add row*, which is the same false claim one keystroke later.
 */
export function hasFormLines(lines: LineForm[]): boolean {
  return lines.some((line) => line.itemName.trim() !== '')
}

// ─── the VAT recapitulation ──────────────────────────────────────────────────

export interface VatBand {
  rate: number
  netCents: number
  /** Summed from the lines — what legacy's recap shows. */
  vatCents: number
  /** The same base taxed once, which is what most suppliers print. */
  wholeBaseVatCents: number
  grossCents: number
}

/**
 * The base and VAT per rate, both ways round.
 *
 * Slovak practice permits per-line or whole-invoice rounding and they differ by a cent or
 * two. Showing both is what turns "the header is wrong" into "the supplier taxes the whole
 * base" — TRADE TECH's 22,01 € against the lines' 22,02 €, which is the entire discrepancy
 * on that invoice.
 */
export function vatBands(lines: InvoiceLine[]): VatBand[] {
  const bands = new Map<number, VatBand>()

  for (const line of lines) {
    const rate = rateOf(line.vatRate)
    const totals = statedLineTotals(line)
    const band =
      bands.get(rate) ?? { rate, netCents: 0, vatCents: 0, wholeBaseVatCents: 0, grossCents: 0 }
    band.netCents += totals.netCents
    band.vatCents += totals.vatCents
    band.grossCents += totals.grossCents
    bands.set(rate, band)
  }

  for (const band of bands.values()) {
    band.wholeBaseVatCents = percentageOfCents(band.netCents, band.rate)
  }

  return [...bands.values()].sort((left, right) => left.rate - right.rate)
}

/** The VAT the whole-base rule gives, summed over the rates. */
export function wholeBaseVatCents(lines: InvoiceLine[]): number {
  return vatBands(lines).reduce((total, band) => total + band.wholeBaseVatCents, 0)
}

/** Whether taxing the whole base explains a header that disagrees with the lines. */
export function wholeBaseExplainsDelta(invoice: InvoiceDetail): boolean {
  const lines = invoice.lines ?? []
  if (lines.length === 0) return false
  const header = headerTotals(invoice)
  const sum = lineSum(lines)
  if (header.netCents !== sum.netCents) return false
  return header.vatCents === wholeBaseVatCents(lines) && header.vatCents !== sum.vatCents
}

export function isMixedRate(lines: InvoiceLine[]): boolean {
  return vatBands(lines).length > 1
}

// ─── dates ───────────────────────────────────────────────────────────────────

/** Postgres prints `2026-05-05 10:00:00+00`; `Date.parse` refuses it twice. */
export function parseStamp(value: string | null | undefined): number | null {
  const text = (value ?? '').trim()
  if (text === '') return null
  const parsed = Date.parse(text.replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00'))
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * How many days past the due date, or `null` when it is not past it.
 *
 * Counted to the end of the due date, because an invoice due today is not late today.
 * Legacy colours the date red and says nothing about how late.
 */
export function daysOverdue(invoice: InvoiceRow, now: number): number | null {
  if (invoice.status === 'cancelled') return null
  if ((invoice.paidAt ?? null) !== null || invoice.status === 'paid') return null

  const due = Date.parse(`${invoice.dueDate ?? ''}T23:59:59`)
  if (!Number.isFinite(due) || due >= now) return null
  return Math.floor((now - due) / 86_400_000)
}

/** Days until it falls due — for the ones that have not yet. */
export function daysUntilDue(invoice: InvoiceRow, now: number): number | null {
  if (invoice.status === 'cancelled' || invoice.status === 'paid') return null
  if ((invoice.paidAt ?? null) !== null) return null

  const due = Date.parse(`${invoice.dueDate ?? ''}T23:59:59`)
  if (!Number.isFinite(due) || due < now) return null
  // Floored, so an invoice due at the end of today reads as nought days left rather than
  // one — the number somebody wants is how many whole days they still have.
  return Math.floor((due - now) / 86_400_000)
}

export function isUnpaid(invoice: InvoiceRow): boolean {
  return (
    invoice.status !== 'cancelled' && invoice.status !== 'paid' && (invoice.paidAt ?? null) === null
  )
}

// ─── approval ────────────────────────────────────────────────────────────────

/**
 * Whether this invoice is over the threshold that needs a main administrator's approval.
 *
 * **`null` is *no gate configured*; `0` is *a gate that admits everything*.** The server used to answer
 * `0` for both — it filtered on `signum() > 0` and fell back to zero — so this had to read zero as
 * *unset*, and a franchise deliberately gating at nothing could not be expressed. It answers `null` for
 * unset now (`MIGRATION-STATUS` FB-135), so the two are distinct here as well.
 *
 * Compared in cents, and **strictly greater** — an invoice exactly on the threshold is not over it,
 * which is what both sides have always done. Only the unset case changed. A gate at zero now catches
 * every invoice with a positive total, which is the useful reading of *gate at nothing*.
 */
export function needsApproval(invoice: InvoiceRow, thresholdCents: number | null): boolean {
  if (thresholdCents === null) return false
  return headerTotals(invoice).grossCents > thresholdCents
}

export function isApproved(invoice: InvoiceRow): boolean {
  return (invoice.approvedAt ?? null) !== null
}

/**
 * Whether the Confirm button may be offered.
 *
 * **The server does not enforce this.** Legacy's confirm answers 403 for an unapproved
 * invoice over the threshold; `InvoiceService.confirm` sets the status and checks nothing,
 * so the gate exists only as the advisory `GET /invoices/{id}/approval-status`. Filed
 * against the backend — until it lands, this screen is the gate, so it holds the line
 * rather than leaving the button live.
 */
export function canConfirm(invoice: InvoiceRow, thresholdCents: number | null): boolean {
  if (invoice.status !== 'draft') return false
  return !needsApproval(invoice, thresholdCents) || isApproved(invoice)
}

export function blockedByApproval(invoice: InvoiceRow, thresholdCents: number | null): boolean {
  return invoice.status === 'draft' && needsApproval(invoice, thresholdCents) && !isApproved(invoice)
}

// ─── what confirming does not do ─────────────────────────────────────────────

/**
 * **FB-091.** Confirming an invoice used to receive the goods. Now it does not.
 *
 * Legacy's `POST /invoices/:id/confirm` resolved unmapped lines through
 * `supplier_item_codes`, learned the mappings a person picked by hand, appended to
 * `supplier_item_price_history`, created a `stock_receipts` row tagged `invoice:<id>`,
 * inserted a stock movement per mapped line and bumped `stock_items.qty` alongside
 * `cost_price` and `cost_unit`. The restored dump still carries receipt 2 with
 * `notes = 'invoice:2'` for Bidfood's `126103376`, so this ran in production.
 *
 * `InvoiceService.confirm` is four lines and sets `status = 'confirmed'`.
 *
 * **Ported 2026-08-02.** `confirm` does all five again — it resolves, learns, appends the
 * price history, creates the receipt and the movements, and refuses over the approval gate
 * with a 403 — so this count stopped being *the size of a gap* and became *what is about to
 * happen*. An unmapped line is now the only part that will not be stocked, which is work
 * somebody has to go and do, and this is the screen that knows it.
 */
export interface StockImpact {
  mappedLines: number
  unmappedLines: number
  totalLines: number
}

/** What the confirm will do to the shelf, in the numbers this page already has. */
export function stockImpactNote(impact: StockImpact): string {
  if (impact.totalLines === 0) return ''
  if (impact.unmappedLines === 0) {
    return tt('Potvrdenie ich prijme na sklad a obnoví nákupnú cenu.')
  }
  if (impact.mappedLines === 0) {
    return tt('Potvrdenie neprijme nič - žiadny riadok nemá skladovú položku.')
  }
  return `${tt('Potvrdenie prijme na sklad')} ${formatCount(impact.mappedLines)} ${tt('z nich; zvyšok ostane nepriradený.')}`
}

/**
 * What the confirm did, once it has done it.
 *
 * The endpoint answers the counts legacy answered — how many lines it resolved from the
 * supplier's codes, how many mappings it learned, how many it could not place, and whether
 * the delivery had already been received. Said out loud rather than swallowed into *saved*,
 * because each of those is something a person may need to act on.
 */
export interface ConfirmOutcome {
  autoResolvedLines: number
  learnedMappings: number
  stockMovementsCreated: number
  unmappedLines: number
  receiptAlreadyExisted: boolean
}

export function confirmOutcomeOf(payload: unknown): ConfirmOutcome {
  const row = (payload ?? {}) as Record<string, unknown>
  const count = (value: unknown): number => (typeof value === 'number' ? value : 0)
  return {
    autoResolvedLines: count(row.autoResolvedLines),
    learnedMappings: count(row.learnedMappings),
    stockMovementsCreated: count(row.stockMovementsCreated),
    unmappedLines: count(row.unmappedLines),
    receiptAlreadyExisted: row.receiptAlreadyExisted === true,
  }
}

export function confirmOutcomeMessage(outcome: ConfirmOutcome): string {
  if (outcome.receiptAlreadyExisted) {
    // The idempotency guard did its job. Worth saying, because otherwise a confirm that stocked
    // nothing looks identical to one that stocked everything.
    return tt('Táto faktúra už bola prijatá na sklad - druhýkrát sa nič nepripísalo.')
  }
  const parts: string[] = [
    `${tt('Prijaté na sklad')}: ${formatCount(outcome.stockMovementsCreated)} ${tt('riadkov')}`,
  ]
  if (outcome.autoResolvedLines > 0) {
    parts.push(`${tt('priradené podľa kódov')}: ${formatCount(outcome.autoResolvedLines)}`)
  }
  if (outcome.learnedMappings > 0) {
    parts.push(`${tt('nových kódov zapamätaných')}: ${formatCount(outcome.learnedMappings)}`)
  }
  if (outcome.unmappedLines > 0) {
    parts.push(`${tt('bez skladovej položky')}: ${formatCount(outcome.unmappedLines)}`)
  }
  return `${parts.join(', ')}.`
}

export function stockImpact(lines: InvoiceLine[]): StockImpact {
  let mapped = 0
  for (const line of lines) {
    if ((line.stockItemId ?? null) !== null) mapped += 1
  }
  return { mappedLines: mapped, unmappedLines: lines.length - mapped, totalLines: lines.length }
}

/**
 * The supplier codes on this invoice that nothing has resolved yet.
 *
 * Every line on every invoice in the dump has `stock_item_id = NULL`, and three of LUNYS's
 * six codes already have a mapping sitting in `supplier_item_codes` — `055008253` to
 * mozzarella, `022601055` to potatoes, `055018115` to spaghetti. Legacy resolved those at
 * confirm time. AGOS resolves them at PDF-parse time and nowhere else, so an invoice typed
 * by hand, or one whose code was mapped after the parse, stays unmapped for good.
 *
 * `GET /suppliers/{supplierId}/resolve-code/{code}` is the lookup, and it is the missing
 * step this list feeds.
 */
export function unresolvedCodes(lines: LineForm[]): string[] {
  const codes = new Set<string>()
  for (const line of lines) {
    const code = line.supplierItemCode.trim()
    if (code !== '' && line.stockItemId.trim() === '') codes.add(code)
  }
  return [...codes]
}

// ─── the SEPA batch ──────────────────────────────────────────────────────────

export type SepaSkipReason = 'paid' | 'cancelled' | 'no-iban' | 'queued'

/**
 * Why an invoice would be left out of the batch — the four rules
 * `AutoPayService.plan` applies, checked here so the answer arrives before the
 * download rather than as a count in a header afterwards.
 *
 * **In the server's order, deliberately.** This screen predicts a decision it does not make;
 * a different order here would name a different reason for the same invoice, and the manager
 * would be told one thing before the download and another after it.
 *
 * `queued` is last for the reason it is last on the server: an invoice that reached a batch
 * had an IBAN, so it only ever catches rows the other three passed — `BACKEND.md` §10A.19.
 */
export function sepaSkipReason(invoice: InvoiceRow): SepaSkipReason | null {
  if ((invoice.paidAt ?? null) !== null) return 'paid'
  if (invoice.status === 'cancelled') return 'cancelled'
  if ((invoice.iban ?? '').trim() === '') return 'no-iban'
  if ((invoice.queuedAt ?? null) !== null) return 'queued'
  return null
}

/**
 * Whether this invoice can be taken back out of a batch — `BACKEND.md` §10A.20, `FRONTEND.md` §9.76.
 *
 * Only a queued one. A paid, cancelled or IBAN-less invoice has nothing to withdraw *from*, and
 * offering the action there would suggest it does something it does not.
 */
export function canWithdrawFromBatch(invoice: InvoiceRow): boolean {
  return sepaSkipReason(invoice) === 'queued'
}

/**
 * What the confirmation says before an invoice is pulled out of a batch — §9.76.
 *
 * **It states the claim rather than asking whether somebody is sure.** The manager is asserting
 * something AGOS cannot check — whether a downloaded file reached the bank is outside this software
 * — and if they are wrong the supplier is paid twice. *Are you sure? This cannot be undone* teaches
 * a person to press the second button without reading, which is what a rare and dangerous action
 * cannot afford.
 *
 * The batch's own date is named because it is the one fact that jogs the memory: *did I upload the
 * seventh's file?* is answerable, where *is this invoice queued?* is not.
 */
export function withdrawWarning(invoice: InvoiceRow): string {
  const queuedAt = (invoice.queuedAt ?? '').trim()
  const when = queuedAt === '' ? null : formatDay(queuedAt)
  const batch = when === null ? '' : ` ${tt('Dávka z')} ${when}.`
  return (
    `${tt('Áno, len ak sa dávka nikdy nenahrala do banky. Ak sa nahrala, faktúra pôjde do ďalšej dávky a dodávateľ dostane zaplatené dvakrát.')}` +
    `${batch} ${tt('Dôvod sa uloží k faktúre.')}`
  )
}

/** The server refuses a blank reason, so the button does too — the refusal is never a surprise. */
export function withdrawReasonAccepted(reason: string): boolean {
  return reason.trim() !== ''
}

export function sepaEligible(invoice: InvoiceRow): boolean {
  return sepaSkipReason(invoice) === null
}

/**
 * Whether the payee IBAN would survive its own checksum.
 *
 * The bank rejects the batch *after* it is uploaded and approved, naming a transaction
 * rather than an invoice. All four supplier accounts in this data pass; the payee account
 * on `/qr-payment` did not (FB-086), which is why this is checked here too.
 */
export function payeeIbanValid(invoice: InvoiceRow): boolean {
  const iban = (invoice.iban ?? '').trim()
  return iban === '' || isValidIban(iban)
}

export interface BatchPlan {
  included: InvoiceRow[]
  skipped: { invoice: InvoiceRow; reason: SepaSkipReason }[]
  suspectIbans: InvoiceRow[]
  grossCents: number
}

/** What the file would contain, and what it would not. */
export function planBatch(invoices: InvoiceRow[], selected: ReadonlySet<number>): BatchPlan {
  const included: InvoiceRow[] = []
  const skipped: { invoice: InvoiceRow; reason: SepaSkipReason }[] = []
  const suspectIbans: InvoiceRow[] = []
  let grossCents = 0

  for (const invoice of invoices) {
    if (invoice.id === undefined || !selected.has(invoice.id)) continue
    const reason = sepaSkipReason(invoice)
    if (reason !== null) {
      skipped.push({ invoice, reason })
      continue
    }
    included.push(invoice)
    grossCents += headerTotals(invoice).grossCents
    if (!payeeIbanValid(invoice)) suspectIbans.push(invoice)
  }

  return { included, skipped, suspectIbans, grossCents }
}

/** Every unpaid invoice the batch would accept — legacy's *select all* button. */
export function payableIds(invoices: InvoiceRow[]): number[] {
  return invoices
    .filter((invoice) => invoice.id !== undefined && sepaEligible(invoice))
    .map((invoice) => invoice.id as number)
}

export interface SenderAccount {
  ibanFrom?: string | null
}

export type SenderProblem = 'missing' | 'checksum' | null

/**
 * Whether a batch can be built at all.
 *
 * `buildSepaBatch` refuses with *"Najprv nastavte IBAN odosielateľa"* when the sender
 * account is unset — and it is unset in this data. Legacy shows the button, lets somebody
 * tick twenty invoices and only then answers with a toast.
 */
export function senderProblem(account: SenderAccount | undefined): SenderProblem {
  const iban = (account?.ibanFrom ?? '').trim()
  if (iban === '') return 'missing'
  if (!isValidIban(iban)) return 'checksum'
  return null
}

/** Tomorrow, because banks refuse a batch asking to be executed today. */
export function defaultExecutionDate(now: number): string {
  const date = new Date(now)
  date.setDate(date.getDate() + 1)
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

export function sepaFileName(executionDate: string): string {
  return `sepa-batch-${executionDate}.xml`
}

/**
 * Whether the XML the server returned is the file it claims to be.
 *
 * A payment file that silently downloaded as an HTML error page is worse than a failed
 * download: it reaches the bank's upload form before anybody reads it.
 */
export function looksLikeSepaXml(body: string): boolean {
  return body.includes('pain.001.001.03') && body.includes('<CdtTrfTxInf>')
}

/** How many payments the returned file actually carries. */
export function paymentCountIn(xml: string): number {
  return xml.split('<CdtTrfTxInf>').length - 1
}

// ─── payment attempts ────────────────────────────────────────────────────────

export interface PaymentAttempt {
  id?: number
  attemptAt?: string | null
  status?: string
  provider?: string
  externalRef?: string | null
  errorMessage?: string | null
}

/**
 * The attempts against one invoice, newest first.
 *
 * Downloading a batch **writes**: `buildSepaBatch` records a `queued` attempt per payment,
 * because whether the file is then uploaded and approved is out of this system's hands.
 * `GET /invoices/{invoiceId}/payment-attempts` exists and legacy never calls it, so
 * nothing stops somebody building a second batch for an invoice already in the first and
 * paying the supplier twice.
 */
export function byMostRecent(attempts: PaymentAttempt[]): PaymentAttempt[] {
  return [...attempts].sort((left, right) => (parseStamp(right.attemptAt) ?? 0) - (parseStamp(left.attemptAt) ?? 0))
}

export function queuedAttempts(attempts: PaymentAttempt[]): PaymentAttempt[] {
  return attempts.filter((attempt) => attempt.status === 'queued')
}

export function attemptLabel(attempt: PaymentAttempt): string {
  if (attempt.status === 'queued') return tt('zaradená do SEPA dávky')
  if (attempt.status === 'paid') return tt('zaplatená')
  // §10A.20. Named before the fall-through, because a withdrawn attempt is not a failed payment:
  // nothing was tried and nothing went wrong. The log's whole purpose is to say what happened to
  // this invoice, and calling a deliberate withdrawal a failure is the one thing it must not say.
  if (attempt.status === 'withdrawn') return tt('stiahnutá z dávky')
  return tt('pokus zlyhal')
}

// ─── the list ────────────────────────────────────────────────────────────────

export interface InvoiceFilter {
  status: string
  supplierId: string
  search: string
}

export function matchesSearch(invoice: InvoiceRow, search: string): boolean {
  const needle = search.trim().toLowerCase()
  if (!needle) return true
  return [invoice.invoiceNumber, invoice.supplierName, invoice.variableSymbol, invoice.notes].some(
    (field) => String(field ?? '').toLowerCase().includes(needle),
  )
}

/**
 * The status filter, including the one the database has no column for.
 *
 * `unpaid` means no payment date and not cancelled — the same definition
 * `InvoiceService.list` applies, kept identical so the count on the tab and the rows behind
 * it cannot disagree.
 */
export function filterInvoices(invoices: InvoiceRow[], filter: InvoiceFilter): InvoiceRow[] {
  return invoices.filter((invoice) => {
    if (filter.status === 'unpaid') {
      if (!isUnpaid(invoice)) return false
    } else if (filter.status !== 'all' && invoice.status !== filter.status) {
      return false
    }
    if (filter.supplierId !== 'all' && String(invoice.supplierId ?? '') !== filter.supplierId) {
      return false
    }
    return matchesSearch(invoice, filter.search)
  })
}

/** Overdue first and by how much, then what falls due soonest, then newest. */
export function byAttention(invoices: InvoiceRow[], now: number): InvoiceRow[] {
  return [...invoices].sort((left, right) => {
    const leftLate = daysOverdue(left, now) ?? -1
    const rightLate = daysOverdue(right, now) ?? -1
    if (leftLate !== rightLate) return rightLate - leftLate

    const leftDue = daysUntilDue(left, now)
    const rightDue = daysUntilDue(right, now)
    if (leftDue !== rightDue) {
      if (leftDue === null) return 1
      if (rightDue === null) return -1
      return leftDue - rightDue
    }

    return (right.issueDate ?? '').localeCompare(left.issueDate ?? '')
  })
}

export interface ListTotals {
  count: number
  grossCents: number
  unpaidCount: number
  unpaidCents: number
  overdueCount: number
  overdueCents: number
  /** Invoices whose header does not add up, or does not match their lines. */
  suspectCount: number
}

export function listTotals(invoices: InvoiceRow[], now: number): ListTotals {
  let grossCents = 0
  let unpaidCount = 0
  let unpaidCents = 0
  let overdueCount = 0
  let overdueCents = 0
  let suspectCount = 0

  for (const invoice of invoices) {
    const gross = headerTotals(invoice).grossCents
    grossCents += gross
    if (isUnpaid(invoice)) {
      unpaidCount += 1
      unpaidCents += gross
    }
    if (daysOverdue(invoice, now) !== null) {
      overdueCount += 1
      overdueCents += gross
    }
    if (!headerAddsUp(invoice)) suspectCount += 1
  }

  return {
    count: invoices.length,
    grossCents,
    unpaidCount,
    unpaidCents,
    overdueCount,
    overdueCents,
    suspectCount,
  }
}

// ─── the form ────────────────────────────────────────────────────────────────

export interface LineForm {
  supplierItemCode: string
  stockItemId: string
  itemName: string
  countryOfOrigin: string
  qty: string
  unit: string
  unitPrice: string
  vatRate: string
  /** The supplier's own figures, kept until somebody edits the line. */
  statedNet: string
  statedVat: string
  statedGross: string
  /** True once qty, price or rate has been touched — then the arithmetic is ours. */
  recomputed: boolean
  confidence: number | null
}

export interface HeaderForm {
  supplierId: string
  invoiceNumber: string
  variableSymbol: string
  constantSymbol: string
  specificSymbol: string
  issueDate: string
  dueDate: string
  deliveryDate: string
  paymentForm: string
  iban: string
  notes: string
  /** The supplier's totals, as read off the document or its PDF. */
  totalNet: string
  totalVat: string
  totalGross: string
}

export function emptyLineForm(defaultRate: string): LineForm {
  return {
    supplierItemCode: '',
    stockItemId: '',
    itemName: '',
    countryOfOrigin: '',
    qty: '1',
    unit: 'ks',
    unitPrice: '0',
    vatRate: defaultRate,
    statedNet: '',
    statedVat: '',
    statedGross: '',
    recomputed: true,
    confidence: null,
  }
}

export function lineFormOf(line: InvoiceLine): LineForm {
  return {
    supplierItemCode: line.supplierItemCode ?? '',
    stockItemId: line.stockItemId === null || line.stockItemId === undefined ? '' : String(line.stockItemId),
    itemName: line.itemName ?? '',
    countryOfOrigin: line.countryOfOrigin ?? '',
    qty: String(rateOf(line.qty)),
    unit: line.unit ?? 'ks',
    unitPrice: String(rateOf(line.unitPriceAfterDiscount)),
    vatRate: String(rateOf(line.vatRate)),
    statedNet: String(line.lineNet ?? ''),
    statedVat: String(line.lineVat ?? ''),
    statedGross: String(line.lineGross ?? ''),
    recomputed: false,
    confidence: typeof line.confidence === 'number' ? line.confidence : null,
  }
}

export function headerFormOf(invoice: InvoiceDetail): HeaderForm {
  return {
    supplierId: invoice.supplierId === null || invoice.supplierId === undefined ? '' : String(invoice.supplierId),
    invoiceNumber: invoice.invoiceNumber ?? '',
    variableSymbol: invoice.variableSymbol ?? '',
    constantSymbol: invoice.constantSymbol ?? '',
    specificSymbol: invoice.specificSymbol ?? '',
    issueDate: invoice.issueDate ?? '',
    dueDate: invoice.dueDate ?? '',
    deliveryDate: invoice.deliveryDate ?? '',
    paymentForm: invoice.paymentForm ?? '',
    iban: invoice.iban ?? '',
    notes: invoice.notes ?? '',
    totalNet: String(invoice.totalNet ?? ''),
    totalVat: String(invoice.totalVat ?? ''),
    totalGross: String(invoice.totalGross ?? ''),
  }
}

/** A form row as a line the totals functions understand. */
export function lineOf(form: LineForm): InvoiceLine {
  const line: InvoiceLine = {
    supplierItemCode: form.supplierItemCode.trim() || null,
    stockItemId: form.stockItemId.trim() === '' ? null : Number(form.stockItemId),
    itemName: form.itemName.trim(),
    countryOfOrigin: form.countryOfOrigin.trim() || null,
    qty: numberOf(form.qty),
    unit: form.unit.trim() || 'ks',
    unitPriceAfterDiscount: numberOf(form.unitPrice),
    vatRate: numberOf(form.vatRate),
    confidence: form.confidence,
  }
  if (form.recomputed) {
    const totals = computedLineTotals(line)
    return {
      ...line,
      lineNet: totals.netCents / 100,
      lineVat: totals.vatCents / 100,
      lineGross: totals.grossCents / 100,
    }
  }
  return { ...line, lineNet: form.statedNet, lineVat: form.statedVat, lineGross: form.statedGross }
}

function numberOf(raw: string): number {
  const parsed = Number(raw.trim().replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : 0
}

/** Editing a quantity, a price or a rate hands the arithmetic to us; nothing else does. */
export function withEdit(form: LineForm, patch: Partial<LineForm>): LineForm {
  const recomputes = 'qty' in patch || 'unitPrice' in patch || 'vatRate' in patch
  return {
    ...form,
    ...patch,
    recomputed: form.recomputed || recomputes,
    // Legacy clears the parser's confidence on any edit, so a corrected row stops asking
    // to be checked. Same here.
    confidence: Object.keys(patch).length > 0 ? null : form.confidence,
  }
}

export type FormProblem =
  | { kind: 'no-supplier' }
  | { kind: 'no-number' }
  | { kind: 'no-lines' }
  | { kind: 'line-no-name'; index: number }
  | { kind: 'line-no-qty'; index: number }
  | { kind: 'no-total' }
  | { kind: 'iban-checksum'; value: string }
  | { kind: 'due-before-issue' }

export function formProblems(header: HeaderForm, lines: LineForm[]): FormProblem[] {
  const problems: FormProblem[] = []
  if (header.supplierId.trim() === '') problems.push({ kind: 'no-supplier' })
  if (header.invoiceNumber.trim() === '') problems.push({ kind: 'no-number' })

  const named = lines.filter((line) => line.itemName.trim() !== '')
  if (named.length === 0) problems.push({ kind: 'no-lines' })

  lines.forEach((line, index) => {
    if (line.itemName.trim() === '') return
    if (numberOf(line.qty) <= 0) problems.push({ kind: 'line-no-qty', index })
  })

  if (parseMoneyToCents(header.totalGross) <= 0 && lineSum(named.map(lineOf)).grossCents <= 0) {
    problems.push({ kind: 'no-total' })
  }

  const iban = header.iban.trim()
  if (iban !== '' && !isValidIban(iban)) problems.push({ kind: 'iban-checksum', value: iban })

  if (header.issueDate !== '' && header.dueDate !== '' && header.dueDate < header.issueDate) {
    problems.push({ kind: 'due-before-issue' })
  }

  return problems
}

export function canSave(problems: FormProblem[]): boolean {
  return !problems.some(
    (problem) =>
      problem.kind === 'no-supplier' ||
      problem.kind === 'no-number' ||
      problem.kind === 'no-lines' ||
      problem.kind === 'line-no-qty' ||
      problem.kind === 'no-total',
  )
}

/**
 * The header body, with the supplier's own totals.
 *
 * **FB-090's fix is this function.** Legacy sends the sum of the lines here. What goes
 * across is what the operator has in front of them as the document's total — untouched
 * unless they pressed the button that says it should be replaced.
 */
export function headerPayload(header: HeaderForm): Record<string, unknown> {
  return {
    supplierId: Number(header.supplierId),
    invoiceNumber: header.invoiceNumber.trim(),
    variableSymbol: header.variableSymbol.trim() || null,
    constantSymbol: header.constantSymbol.trim() || null,
    specificSymbol: header.specificSymbol.trim() || null,
    issueDate: header.issueDate || null,
    dueDate: header.dueDate || null,
    deliveryDate: header.deliveryDate || null,
    paymentForm: header.paymentForm.trim() || null,
    // Stored unspaced so one account is one string, as `/banking-settings` does.
    iban: header.iban.replace(/\s/g, '').toUpperCase() || null,
    notes: header.notes.trim() || null,
    currency: 'EUR',
    totalNet: centsToApi(parseMoneyToCents(header.totalNet)),
    totalVat: centsToApi(parseMoneyToCents(header.totalVat)),
    totalGross: centsToApi(parseMoneyToCents(header.totalGross)),
  }
}

/** `lineNet`, `unitPriceAfterDiscount` and `lineGross` are all required by the request. */
export function linePayload(form: LineForm, index: number): Record<string, unknown> {
  const line = lineOf(form)
  const totals = statedLineTotals(line)
  return {
    supplierItemCode: line.supplierItemCode,
    stockItemId: line.stockItemId,
    itemName: line.itemName,
    countryOfOrigin: line.countryOfOrigin,
    qty: rateOf(line.qty),
    unit: line.unit,
    unitPriceAfterDiscount: rateOf(line.unitPriceAfterDiscount),
    vatRate: rateOf(line.vatRate),
    lineNet: centsToApi(totals.netCents),
    lineVat: centsToApi(totals.vatCents),
    lineGross: centsToApi(totals.grossCents),
    sortOrder: index,
  }
}

export function linePayloads(lines: LineForm[]): Record<string, unknown>[] {
  return lines
    .filter((line) => line.itemName.trim() !== '')
    .map((line, index) => linePayload(line, index))
}

/**
 * Cents back to the number the request wants; never a float built by multiplication.
 *
 * `toFixed` here is a **wire** format, not a display one — the value goes into a JSON body, where a decimal
 * point is the only thing the server will parse. N-02's rule is about what reaches a Slovak screen.
 */
function centsToApi(cents: number): number {
  // eslint-disable-next-line no-restricted-syntax -- wire format: JSON number, not a rendered figure
  return Number((cents / 100).toFixed(2))
}

/** The totals the lines come to, as a header form — for the *use the lines* button. */
export function totalsFromLines(lines: LineForm[]): Pick<HeaderForm, 'totalNet' | 'totalVat' | 'totalGross'> {
  const sum = lineSum(lines.filter((line) => line.itemName.trim() !== '').map(lineOf))
  return {
    // Form-field values, not rendered figures: a numeric input holds `12.50` and a comma would make it
    // invalid. N-02's rule is about the screen; this is what the screen's inputs parse.
    /* eslint-disable no-restricted-syntax -- form values, parsed by the input and by the API */
    totalNet: (sum.netCents / 100).toFixed(2),
    totalVat: (sum.vatCents / 100).toFixed(2),
    totalGross: (sum.grossCents / 100).toFixed(2),
    /* eslint-enable no-restricted-syntax */
  }
}

/** What the header on the form differs from the lines by, while it is being typed. */
export function formDelta(header: HeaderForm, lines: LineForm[]): HeaderDelta {
  const sum = lineSum(lines.filter((line) => line.itemName.trim() !== '').map(lineOf))
  const delta = {
    netCents: parseMoneyToCents(header.totalNet) - sum.netCents,
    vatCents: parseMoneyToCents(header.totalVat) - sum.vatCents,
    grossCents: parseMoneyToCents(header.totalGross) - sum.grossCents,
  }
  return {
    ...delta,
    agrees: delta.netCents === 0 && delta.vatCents === 0 && delta.grossCents === 0,
  }
}

// ─── the PDF import ──────────────────────────────────────────────────────────

export interface ParsedInvoice {
  invoiceNumber?: string | null
  variableSymbol?: string | null
  constantSymbol?: string | null
  specificSymbol?: string | null
  issueDate?: string | null
  dueDate?: string | null
  deliveryDate?: string | null
  paymentForm?: string | null
  iban?: string | null
  totalNet?: number | null
  totalVat?: number | null
  totalGross?: number | null
  currency?: string | null
  supplierName?: string | null
  supplierIco?: string | null
  supplierMatch?: { id?: number; name?: string } | null
  resolvedLineCount?: number
  warnings?: string[]
  parseMode?: string
  lines?: {
    supplierItemCode?: string | null
    itemName?: string | null
    countryOfOrigin?: string | null
    qty?: number | null
    unit?: string | null
    unitPriceAfterDiscount?: number | null
    vatRate?: number | null
    lineNet?: number | null
    confidence?: number | null
    stockItemId?: number | null
  }[]
}

/** A parser confidence below this asks for a person to look at the row. */
export const REVIEW_CONFIDENCE = 0.7

export function needsReview(line: LineForm): boolean {
  return line.confidence !== null && line.confidence < REVIEW_CONFIDENCE
}

export function reviewCount(lines: LineForm[]): number {
  return lines.filter(needsReview).length
}

/**
 * The parse as a form.
 *
 * The parsed header totals are **kept** — that is FB-090. The parser only reports
 * `lineNet` per row, so the VAT and gross are derived from it at the row's own rate, which
 * is what legacy does and what every supplier in the data prints.
 */
export function formsFromParse(parsed: ParsedInvoice): { header: HeaderForm; lines: LineForm[] } {
  const header: HeaderForm = {
    supplierId: parsed.supplierMatch?.id === undefined ? '' : String(parsed.supplierMatch.id),
    invoiceNumber: parsed.invoiceNumber ?? '',
    variableSymbol: parsed.variableSymbol ?? parsed.invoiceNumber ?? '',
    constantSymbol: parsed.constantSymbol ?? '',
    specificSymbol: parsed.specificSymbol ?? '',
    issueDate: parsed.issueDate ?? '',
    dueDate: parsed.dueDate ?? '',
    deliveryDate: parsed.deliveryDate ?? '',
    paymentForm: parsed.paymentForm ?? '',
    iban: parsed.iban ?? '',
    notes: '',
    totalNet: numberText(parsed.totalNet),
    totalVat: numberText(parsed.totalVat),
    totalGross: numberText(parsed.totalGross),
  }

  const lines: LineForm[] = (parsed.lines ?? []).map((line) => {
    const netCents = parseMoneyToCents(line.lineNet)
    const vatCents = percentageOfCents(netCents, line.vatRate ?? 0)
    return {
      supplierItemCode: line.supplierItemCode ?? '',
      stockItemId: line.stockItemId === null || line.stockItemId === undefined ? '' : String(line.stockItemId),
      itemName: line.itemName ?? '',
      countryOfOrigin: line.countryOfOrigin ?? '',
      qty: String(line.qty ?? 0),
      unit: line.unit ?? 'ks',
      unitPrice: String(line.unitPriceAfterDiscount ?? 0),
      vatRate: String(line.vatRate ?? 0),
      /* eslint-disable no-restricted-syntax -- form values, parsed by the input and by the API */
      statedNet: (netCents / 100).toFixed(2),
      statedVat: (vatCents / 100).toFixed(2),
      statedGross: ((netCents + vatCents) / 100).toFixed(2),
      /* eslint-enable no-restricted-syntax */
      recomputed: false,
      confidence: typeof line.confidence === 'number' ? line.confidence : null,
    }
  })

  return { header, lines }
}

function numberText(value: number | null | undefined): string {
  if (value === null || value === undefined) return ''
  return String(value)
}

/**
 * What the parse could not work out, said plainly.
 *
 * Legacy stuffs these into the invoice's `notes` field prefixed `⚠ Auto-import:` — so a
 * parser complaint becomes part of the accounting record and stays there after it has been
 * dealt with. They belong on the screen while the form is open.
 */
export function parseWarnings(parsed: ParsedInvoice): string[] {
  return (parsed.warnings ?? []).filter((warning) => warning.trim() !== '')
}

export function parseModeLabel(mode: string | undefined): string {
  if (mode === 'coordinates') return tt('podľa rozloženia strany')
  if (mode === 'regex') return tt('náhradným čítaním textu')
  return tt('riadky sa nenašli')
}

/** Only PDFs. Legacy's drop handler already filters, and the file picker should agree. */
export function pdfsOnly(files: File[]): File[] {
  return files.filter(
    (file) => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'),
  )
}

export { formatCents, isValidIco }

/**
 * The books' own totals — §9.256, FB-416.
 *
 * `GET /invoices-stats` answers the unconditional figures the server already computes, and nothing
 * read them: the page summed its **server-filtered** list instead, so choosing *Zaplatené* made
 * *Nezaplatené* read nought while unpaid invoices sat in the books.
 *
 * Money arrives as **euros as a decimal number** — `unpaidTotal: 0.0` measured — not cents and not a
 * string, so it is converted once here.
 */
export interface BookTotals {
  totalCount: number
  unpaidCount: number
  overdueCount: number
  unpaidCents: number
  overdueCents: number
  thisMonthCents: number
  thisYearCents: number
}

export function bookTotalsOf(payload: unknown): BookTotals {
  const row = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>
  const count = (value: unknown): number =>
    typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : 0
  const cents = (value: unknown): number =>
    typeof value === 'number' && Number.isFinite(value) ? Math.round(value * 100) : 0
  return {
    totalCount: count(row.totalCount),
    unpaidCount: count(row.unpaidCount),
    overdueCount: count(row.overdueCount),
    unpaidCents: cents(row.unpaidTotal),
    overdueCents: cents(row.overdueTotal),
    thisMonthCents: cents(row.thisMonthTotal),
    thisYearCents: cents(row.thisYearTotal),
  }
}

/**
 * Said under the tiles when a filter is on — §9.256.
 *
 * Two numbers on one screen that mean different things have to say which is which, or they read as
 * disagreeing. Nothing at all when the list is unfiltered, because *24 of 24* is noise.
 */
export function filteredNote(shownCount: number, totals: BookTotals, filtered: boolean): string {
  if (!filtered) return ''
  return `${tt('Zobrazených')}: ${shownCount} ${tt('z')} ${totals.totalCount}`
}
